import { type Dispatch, type ReactNode, type SetStateAction, useCallback, useEffect, useMemo, useRef } from 'react'
import { ExpenseTab } from './ExpenseTab'

import './expenseTabs.scss'

type TabSetup = {
  title: ReactNode
  name: string
}

type ExpenseTabsProps = {
  config: TabSetup[]
  counts?: Record<string, number>
  currentTab: string
  setTab: Dispatch<SetStateAction<string>>
}

const ExpenseTabs = ({ config, counts, setTab, currentTab }: ExpenseTabsProps) => {
  const carouselRef = useRef(null)

  const activeIndex = useMemo(() => config.findIndex(tab => tab.name === currentTab), [currentTab, config])

  const onMouseDown = useCallback(e => {
    e.preventDefault()

    const drag = mouseOverEvent => {
      document.body.style.cursor = 'pointer'
      if (carouselRef.current) {
        carouselRef.current.scroll({
          left: carouselRef.current.scrollLeft - mouseOverEvent.movementX
        })
      }
    }

    carouselRef.current?.addEventListener('mousemove', drag)

    const cancelDrag = () => {
      carouselRef.current?.removeEventListener('mousemove', drag)
      document.body.style.cursor = 'default'
    }

    carouselRef.current?.addEventListener('mouseup', cancelDrag, { once: true })
    carouselRef.current?.addEventListener('mouseleave', cancelDrag, { once: true })
  }, [])

  useEffect(() => {
    const element = carouselRef.current
    element?.addEventListener('mousedown', onMouseDown)
    return () => {
      element?.removeEventListener('mousedown', onMouseDown)
    }
  }, [onMouseDown])

  return (
    <div className='tabs-wrapper'>
      <div className='tabs-container' ref={carouselRef}>
        {config.map((item, index) => (
          <ExpenseTab
            key={`${item.name}-${index}`}
            active={activeIndex === index}
            caption={item.title}
            count={counts?.[item.name]}
            onClick={() => setTab(item.name)}
          />
        ))}
      </div>
    </div>
  )
}

export default ExpenseTabs
